import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Collaborator, CollaboratorSchema } from '@/models';
import { normalizeCollaboratorFormData } from '@/utils/form-validation/formDataNormalizer';
import { extractValidationErrors } from '@/utils/form-validation/formatValidationErrors';
import { useToast } from '@/hooks/useToast';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { FormTabBase } from '@/components/shared/FormTabBase';
import { ModalHeader } from '@/components/shared';
import { BaseModal } from '@/components/shared/BaseModal';
import { FullFormTabs } from '@/features/Collaborators/Edit/Contexts/FullForm';
import ConfirmDialog from '@/features/Projects/pages/ProjectDetails/components/ObjectivesPhase/components/ConfirmDialog';
import { useVacationDpEditMode, VACATION_DP_EDITABLE_FIELDS } from '@/features/Collaborators/hooks/useVacationDpEditMode';

import { useCollaboratorByIdQuery } from '@/features/Collaborators/Data';

interface CollaboratorFormProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    onSubmit: (data: CollaboratorFormData) => Promise<void>;
    onCancel?: () => void;
    title?: string;
    hideFooter?: boolean;
    isSaving?: boolean;
    hideHeader?: boolean;
    hideNavigationButtons?: boolean;
    maxWidth?: string;
    children?: React.ReactNode;
}

const CollaboratorForm: React.FC<CollaboratorFormProps> = ({
    context,
    collaboratorId,
    onSubmit,
    onCancel,
    title,
    hideFooter = false,
    isSaving = false,
    hideHeader = false,
    hideNavigationButtons = false,
    maxWidth = 'max-w-7xl',
    children
}) => {
    const { isVacationDpOnly } = useVacationDpEditMode();
    const [selectedTab, setSelectedTab] = useState(isVacationDpOnly ? 'vacation' : 'basic-info');
    const [isLoading, setIsLoading] = useState(false);
    const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
    const pendingResyncRef = useRef(false);

    const { data: collaborator, isFetching } = useCollaboratorByIdQuery(collaboratorId);
    const { success, error } = useToast();

    // 🔄 NORMALIZAR dados iniciais para evitar controlled vs uncontrolled
    const getInitialValues = (collaborator: Collaborator | null): CollaboratorFormData => {
        if (!collaborator) {
            return normalizeCollaboratorFormData(collaborator) as CollaboratorFormData;
        }
        return normalizeCollaboratorFormData(collaborator) as CollaboratorFormData;
    };

    const form = useForm<CollaboratorFormData>({
        defaultValues: collaborator ? getInitialValues(collaborator) : undefined,
        resolver: zodResolver(CollaboratorSchema),
    });

    const { control, setValue, watch, handleSubmit, formState: { errors, isSubmitting, isDirty } } = form;

    // Ressincronizar o formulário com dados frescos do servidor após save
    useEffect(() => {
        if (!pendingResyncRef.current || !collaborator || isFetching) return;
        pendingResyncRef.current = false;
        form.reset(getInitialValues(collaborator));
    }, [collaborator, isFetching, form]);

    const handleFormSubmit: SubmitHandler<CollaboratorFormData> = async (data) => {
        console.log('🚀 [handleFormSubmit] INICIADO', data);

        try {
            // 🔍 VALIDAÇÃO ADICIONAL: Verificar se há erros de validação
            // Ignorar erros em campos de metadata/controle que não são preenchidos pelo usuário
            const controlFields = ['updatedStatus', 'updateHistory', 'createdAt', 'updatedAt'];
            const userErrors = Object.keys(errors).filter(key => !controlFields.includes(key));

            console.log('🔍 [handleFormSubmit] Total de erros:', Object.keys(errors).length);
            console.log('🔍 [handleFormSubmit] Erros em campos de controle:', Object.keys(errors).filter(key => controlFields.includes(key)));
            console.log('🔍 [handleFormSubmit] Erros em campos do usuário:', userErrors);

            if (userErrors.length > 0) {
                const errorCount = userErrors.length;
                console.log('❌ [handleFormSubmit] Erros encontrados em campos do usuário:', errorCount, errors);
                error('Erro de validação', `Você tem ${errorCount} erro(s) no formulário. Verifique os campos destacados`);

                // 🔍 LOG DETALHADO DE TODOS OS ERROS DE VALIDAÇÃO
                console.log('❌ FORM VALIDATION ERRORS:', errors);
                const formattedErrors = extractValidationErrors(errors);
                console.log('📋 FORMATTED ERRORS:', formattedErrors);

                return;
            }

            console.log('✅ [handleFormSubmit] Sem erros de validação relevantes, chamando onSubmit...');
            pendingResyncRef.current = true;
            await onSubmit(data);
            console.log('✅ [handleFormSubmit] onSubmit concluído com sucesso');
        } catch (error) {
            console.error('💥 [handleFormSubmit] ERRO:', error);
            pendingResyncRef.current = false;
            throw error;
        }
    };

    const handleBackClick = () => {
        if (isDirty) {
            setShowDiscardConfirm(true);
        } else {
            onCancel?.();
        }
    };

    const handleConfirmDiscard = () => {
        setShowDiscardConfirm(false);
        onCancel?.();
    };

    return (
        <div className="bg-white shadow sm:rounded-lg">
            <FormProvider {...form}>
                <form
                    onSubmit={(e) => {
                        console.log('📝 [FORM] onSubmit disparado', {
                            eventType: e.type,
                            defaultPrevented: e.defaultPrevented,
                            target: e.target,
                            errorsAtSubmit: errors,
                            isSubmittingState: isSubmitting
                        });

                        // Chamar handleSubmit e capturar o resultado
                        const submitHandler = handleSubmit(
                            (data) => {
                                console.log('✅ [RHF] Validação passou, chamando handleFormSubmit');
                                return handleFormSubmit(data);
                            },
                            (errors) => {
                                console.log('❌ [RHF] Validação falhou:', errors);
                            }
                        );

                        console.log('🔵 [FORM] Chamando submitHandler...');
                        submitHandler(e);
                    }}
                    className="space-y-8"
                >


                    <div className="px-4 py-5 sm:p-6 space-y-6">
                        <FullFormTabs
                            context={context}
                            collaboratorId={collaboratorId}
                            form={form}
                            selectedTab={selectedTab}
                            setSelectedTab={setSelectedTab}
                        />

                        {children}
                    </div>

                    {!hideFooter && (
                        <div className="flex justify-end space-x-4 pt-6 border-t px-4 py-5 sm:px-6 sm:py-4">
                            {onCancel && (
                                <button
                                    type="button"
                                    onClick={handleBackClick}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    disabled={isSubmitting}
                                >
                                    Voltar
                                </button>
                            )}
                            <button
                                type="submit"
                                onClick={(e) => {
                                    console.log('🔴 [BOTÃO SALVAR] Click detectado!', {
                                        isSubmitting,
                                        isSaving,
                                        hasErrors: Object.keys(errors).length > 0,
                                        errors,
                                        formElement: e.currentTarget.form,
                                        buttonType: e.currentTarget.type
                                    });
                                    // Não chamar preventDefault - deixar o form submit acontecer naturalmente
                                }}
                                className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                                disabled={isSubmitting}
                            >
                                {isSaving ? 'Salvando...' : 'Salvar'}
                            </button>
                        </div>
                    )}
                </form>
            </FormProvider>

            <ConfirmDialog
                isOpen={showDiscardConfirm}
                type="warning"
                title="Descartar alterações?"
                message="Você tem alterações não salvas. Se sair agora, elas serão perdidas."
                confirmText="Sair sem salvar"
                cancelText="Continuar editando"
                onConfirm={handleConfirmDiscard}
                onCancel={() => setShowDiscardConfirm(false)}
            />
        </div>
    );
};

export default CollaboratorForm; 